import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { FaTwitter, FaTelegram, FaDiscord, FaGithub, FaMedium, FaLinkedin, FaReddit, FaTiktok, FaInstagram, FaPhone, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import Button from '../ui/Button';

// Translation data
const translations = {
  en: {
    quickLinks: 'Quick Links',
    home: 'Home',
    whitepaper: 'Whitepaper',
    roadmap: 'Roadmap',
    team: 'Team & Partners',
    faq: 'FAQ',
    resources: 'Resources',
    documentation: 'Documentation',
    tokenomics: 'Tokenomics',
    presale: 'Presale',
    legal: 'Legal',
    legalDocs: 'Legal Documentation',
    privacyPolicy: 'General Disclaimer',
    termsOfService: 'Terms of Service',
    riskDisclosure: 'Risk Disclosure',
    contactUs: 'Contact Us',
    joinCommunity: 'Join Our Community',
    copyright: '© 2026 Pronova Virtual Asset. All rights reserved.',
    about: 'Pronova (PRN) is a next-generation global digital asset combining blockchain innovation with real-world utility. Designed for trading, payments, tokenized real-world assets (RWA), digital finance, and institutional investment, PRN connects cryptocurrency markets with practical economic applications through an expanding international network of platforms, businesses, and strategic partners.',
    usOffice: 'United States',
    usAddress: 'Pronova Virtual Asset LLC — Registered in Wyoming, USA\nCapimax Group Holding LLC — Delaware, USA',
    ukOffice: 'United Kingdom',
    ukAddress: 'Capimax Technologies Ltd — London, United Kingdom',
    disclaimer: 'Disclaimer: Cryptocurrency investments are subject to market risks. Always DYOR (Do Your Own Research).',
    subscribeTitle: 'Stay Updated',
    subscribeText: 'Get the latest news and updates about Pronova',
    emailPlaceholder: 'Your email address',
    subscribeButton: 'Subscribe',
  },
  ar: {
    quickLinks: 'روابط سريعة',
    home: 'الرئيسية',
    whitepaper: 'الورقة البيضاء',
    roadmap: 'خارطة الطريق',
    team: 'الفريق والشركاء',
    faq: 'الأسئلة الشائعة',
    resources: 'المصادر',
    documentation: 'التوثيق',
    tokenomics: 'توزيع العملة',
    presale: 'البيع التمهيدي',
    legal: 'قانوني',
    legalDocs: 'المستندات القانونية',
    privacyPolicy: 'إخلاء المسؤولية العام',
    termsOfService: 'شروط الخدمة',
    riskDisclosure: 'إفصاح المخاطر',
    contactUs: 'اتصل بنا',
    joinCommunity: 'انضم إلى مجتمعنا',
    copyright: '© 2026 برونوفا. جميع الحقوق محفوظة.',
    about: 'برونوفا (PRN) عملة رقمية عالمية من الجيل الجديد تجمع بين ابتكار البلوكشين والاستخدام الواقعي. مصمّمة للتداول والمدفوعات وترميز الأصول الواقعية (RWA) والتمويل الرقمي والاستثمار المؤسسي، تربط PRN أسواق العملات المشفرة بالتطبيقات الاقتصادية العملية عبر شبكة دولية متنامية من المنصات والشركات والشركاء الاستراتيجيين.',
    usOffice: 'الولايات المتحدة',
    usAddress: 'Pronova Virtual Asset LLC — مسجّلة في وايومنغ، الولايات المتحدة\nCapimax Group Holding LLC — ديلاوير، الولايات المتحدة',
    ukOffice: 'المملكة المتحدة',
    ukAddress: 'Capimax Technologies Ltd — لندن، المملكة المتحدة',
    disclaimer: 'إخلاء المسؤولية: استثمارات العملات المشفرة عرضة لمخاطر السوق. قم دائمًا بإجراء البحث الخاص بك.',
    subscribeTitle: 'ابق على اطلاع',
    subscribeText: 'احصل على آخر الأخبار والتحديثات حول برونوفا',
    emailPlaceholder: 'عنوان بريدك الإلكتروني',
    subscribeButton: 'اشترك',
  },
};

const Footer = () => {
  const { language } = useLanguage();
  const t = translations[language];

  return (
    <footer className="pt-16 pb-8 relative overflow-hidden bg-gray-50 dark:bg-dark-900">
      {/* Background elements */}
      <div className="absolute inset-0 z-0"></div>
      
      {/* Background shapes and effects */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary-600 to-secondary-500 z-1"></div>
      <div className="absolute -top-[200px] -left-[100px] w-[500px] h-[500px] rounded-full bg-primary-600/5 dark:bg-primary-600/10 blur-[120px] z-0"></div>
      <div className="absolute -bottom-[100px] -right-[100px] w-[400px] h-[400px] rounded-full bg-secondary-500/5 dark:bg-secondary-500/10 blur-[120px] z-0"></div>

      <div className="container-custom relative z-10">
        {/* Newsletter Subscription */}
        <div className="mb-16 bg-white dark:bg-dark-900/90 backdrop-blur-sm rounded-2xl p-8 border border-gray-200 dark:border-primary-600/30 shadow-lg">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
            <div className="lg:col-span-1">
              <h3 className="text-xl font-heading font-bold text-gray-900 dark:text-white mb-2">{t.subscribeTitle}</h3>
              <p className="text-gray-600 dark:text-gray-300">{t.subscribeText}</p>
            </div>
            <div className="lg:col-span-2">
              <form className="flex flex-col sm:flex-row gap-3">
                <input 
                  type="email" 
                  placeholder={t.emailPlaceholder} 
                  className="flex-grow px-4 py-3 rounded-full bg-gray-50 dark:bg-dark-800 border border-gray-300 dark:border-primary-600/20 focus:border-primary-500 text-gray-900 dark:text-white outline-none transition-colors duration-300"
                />
                <Button 
                  variant="gradient"
                  size="medium"
                  type="submit"
                >
                  {t.subscribeButton}
                </Button>
              </form>
            </div>
          </div>
        </div>

        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Column 1: Logo & Info */}
          <div>
            <div className="mb-4">
              <span className="text-2xl font-heading font-bold gradient-text">
                Pronova
              </span>
            </div>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              {t.about}
            </p>

            {/* Contact details (client request: clear contact info + phone numbers) */}
            <div className="space-y-2 mb-2">
              <a href="mailto:support@pronovacrypto.com" className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                <FaEnvelope size={14} className="text-primary-500 flex-shrink-0" />
                support@pronovacrypto.com
              </a>
              <a href="tel:+447577367756" className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                <FaPhone size={14} className="text-primary-500 flex-shrink-0" />
                +44 7577 367756 <span className="text-gray-400 text-sm">(UK)</span>
              </a>
              <a href="tel:+12053508864" className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                <FaPhone size={14} className="text-primary-500 flex-shrink-0" />
                +1 205-350-8864 <span className="text-gray-400 text-sm">(US)</span>
              </a>
              <div className="flex items-start gap-2 text-gray-600 dark:text-gray-300">
                <FaMapMarkerAlt size={14} className="text-primary-500 flex-shrink-0 mt-1" />
                <span>
                  <span className="block font-semibold text-gray-700 dark:text-gray-200">{t.usOffice}</span>
                  <span className="block text-sm whitespace-pre-line">{t.usAddress}</span>
                  <span className="block font-semibold text-gray-700 dark:text-gray-200 mt-2">{t.ukOffice}</span>
                  <span className="block text-sm whitespace-pre-line">{t.ukAddress}</span>
                </span>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 mt-6">
              <a 
                href="https://x.com/pronovacryptocu" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-500 dark:text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors p-2 bg-gray-100 dark:bg-dark-800/50 rounded-full border border-gray-200 dark:border-primary-600/10 hover:border-primary-500/30 dark:hover:border-primary-600/30"
                title="Follow us on X (Twitter)"
              >
                <FaTwitter size={18} />
              </a>
              <a 
                href="https://t.me/+g5RtC5kjAfYwODA0" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-500 dark:text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors p-2 bg-gray-100 dark:bg-dark-800/50 rounded-full border border-gray-200 dark:border-primary-600/10 hover:border-primary-500/30 dark:hover:border-primary-600/30"
                title="Join our Telegram"
              >
                <FaTelegram size={18} />
              </a>
              <a 
                href="https://discord.gg/DN8tusJw8C" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-500 dark:text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors p-2 bg-gray-100 dark:bg-dark-800/50 rounded-full border border-gray-200 dark:border-primary-600/10 hover:border-primary-500/30 dark:hover:border-primary-600/30"
                title="Join our Discord"
              >
                <FaDiscord size={18} />
              </a>
              <a 
                href="https://www.reddit.com/user/pronovacryptocurrenc/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-500 dark:text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors p-2 bg-gray-100 dark:bg-dark-800/50 rounded-full border border-gray-200 dark:border-primary-600/10 hover:border-primary-500/30 dark:hover:border-primary-600/30"
                title="Follow us on Reddit"
              >
                <FaReddit size={18} />
              </a>
              <a 
                href="https://www.tiktok.com/@pronova.crypto.cu" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-500 dark:text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors p-2 bg-gray-100 dark:bg-dark-800/50 rounded-full border border-gray-200 dark:border-primary-600/10 hover:border-primary-500/30 dark:hover:border-primary-600/30"
                title="Follow us on TikTok"
              >
                <FaTiktok size={18} />
              </a>
              <a 
                href="https://github.com/pronovacryptocurrency/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-500 dark:text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors p-2 bg-gray-100 dark:bg-dark-800/50 rounded-full border border-gray-200 dark:border-primary-600/10 hover:border-primary-500/30 dark:hover:border-primary-600/30"
                title="View our GitHub"
              >
                <FaGithub size={18} />
              </a>
              <a 
                href="https://www.instagram.com/pronovacryptocurrency/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-500 dark:text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors p-2 bg-gray-100 dark:bg-dark-800/50 rounded-full border border-gray-200 dark:border-primary-600/10 hover:border-primary-500/30 dark:hover:border-primary-600/30"
                title="Follow us on Instagram"
              >
                <FaInstagram size={18} />
              </a>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h3 className="text-lg font-heading font-semibold mb-4 text-gray-900 dark:text-white">{t.quickLinks}</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                  {t.home}
                </Link>
              </li>
              <li>
                <Link to="/whitepaper" className="text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                  {t.whitepaper}
                </Link>
              </li>
              <li>
                <Link to="/roadmap" className="text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                  {t.roadmap}
                </Link>
              </li>
              <li>
                <Link to="/team" className="text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                  {t.team}
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                  {t.faq}
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Resources */}
          <div>
            <h3 className="text-lg font-heading font-semibold mb-4 text-gray-900 dark:text-white">{t.resources}</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/whitepaper" className="text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                  {t.documentation}
                </Link>
              </li>
              <li>
                <a href="#tokenomics" className="text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                  {t.tokenomics}
                </a>
              </li>
              <li>
                <a href="#presale" className="text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                  {t.presale}
                </a>
              </li>
            </ul>
          </div>

          {/* Column 4: Legal */}
          <div>
            <h3 className="text-lg font-heading font-semibold mb-4 text-gray-900 dark:text-white">{t.legal}</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/legal" className="text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                  {t.legalDocs}
                </Link>
              </li>
              <li>
                <Link to="/legal#disclaimer" className="text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                  {t.privacyPolicy}
                </Link>
              </li>
              <li>
                <Link to="/legal#risk" className="text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                  {t.riskDisclosure}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                  {t.contactUs}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-300 dark:border-primary-600/20 pt-8">
          <div className="flex flex-col md:flex-row md:justify-center items-center">
            <div className="text-gray-500 dark:text-gray-400 text-sm">
              {t.copyright}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;